"use client";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";

const BotVideoSectiontwo = ({ data }) => {
  // Fallback to hardcoded values if data is not available
  const title = data?.title || "تقنيات متطورة";
  const titleHighlight = data?.subtitle || "للإعلان الذكي";
  const description =
    data?.description ||
    "نستخدم أحدث التقنيات والذكاء الاصطناعي لضمان وصول رسالتك الإعلانية للجمهور المناسب في الوقت المناسب بأكثر الطرق فعالية.";
  const videoUrl =
    data?.video?.url ||
    data?.videoUrl ||
    "https://pub-0705ed3bb3a5425799ba64b1bd76a81b.r2.dev/shared_media/pob-robot.mp4";

  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const videoElementRef = useRef(null);
  const [particles, setParticles] = useState([]);
  const [isMuted, setIsMuted] = useState(false); // Start unmuted by default

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const videoScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 1.2]);

  // Check if video is in viewport - keep checking as user scrolls
  const isInView = useInView(sectionRef, {
    amount: 0.5, // Video must be at least 50% visible
    once: false, // Keep checking as user scrolls
  });

  // Generate particles on client side to avoid hydration mismatch
  useEffect(() => {
    const generatedParticles = Array.from({ length: 8 }, (_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 4 + Math.random() * 2,
    }));
    setParticles(generatedParticles);
  }, []);

  // Control video muted state based on viewport visibility
  useEffect(() => {
    const video = videoElementRef.current;
    if (!video) return;

    if (isInView) {
      // Try to play unmuted; if blocked by browser, fallback to muted autoplay so the video actually plays
      try {
        video.muted = false;
        setIsMuted(false);
      } catch (e) {}

      try {
        if (typeof video.currentTime === "number") video.currentTime = 0;
      } catch (e) {}

      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          // Autoplay with sound blocked by browser policy. Fall back to muted autoplay so playback continues.
          try {
            video.muted = true;
            setIsMuted(true);
            if (typeof video.currentTime === "number") video.currentTime = 0;
            video.play().catch(() => {
              console.warn("Muted autoplay also failed:", err);
            });
          } catch (err2) {
            console.warn("Error during muted fallback:", err2);
          }
        });
      }
    } else {
      // Pause when out of view to save resources
      try {
        video.pause();
      } catch (e) {}
    }
  }, [isInView]);

  const toggleMute = () => {
    if (videoElementRef.current) {
      videoElementRef.current.muted = !videoElementRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  // Note: intentionally removed the first-interaction unmute fallback per UX request.

  return (
    <section
      ref={sectionRef}
      className="relative py-40 overflow-hidden"
      style={{
        background: "linear-gradient(0deg, #0C0927 0%, #233C78 100%)",
      }}
    >
      {/* Animated Background */}
      <motion.div className="absolute inset-0" style={{ y: backgroundY }}>
        {/* Animated Background Elements */}
        <motion.div
          className="absolute top-10 right-10 w-40 h-40 bg-[#dfa428]/10 rounded-full"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
            x: [0, 50, 0],
          }}
          transition={{ duration: 12, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-10 left-10 w-32 h-32 bg-white/5 rounded-full"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
            y: [0, -30, 0],
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute top-1/3 left-1/3 w-24 h-24 bg-[#dfa428]/15 transform rotate-45"
          animate={{
            rotate: [45, 225, 405],
            scale: [1, 1.4, 1],
            opacity: [0.3, 0.7, 0.3],
          }}
          transition={{ duration: 6, repeat: Infinity }}
        />

        {/* Floating Particles */}
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute w-2 h-2 bg-[#dfa428]/40 rounded-full"
            style={{
              top: `${particle.top}%`,
              left: `${particle.left}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              delay: particle.delay,
            }}
          />
        ))}
      </motion.div>

      {/* Content */}
      <motion.div
        className="relative z-10 container mx-auto px-6"
        style={{ y: contentY }}
      >
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Video */}
          <motion.div
            ref={videoRef}
            className="relative"
            style={{ scale: videoScale }}
            initial={{ opacity: 0, x: -100 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1.2 }}
          >
            <div className="relative overflow-hidden rounded-3xl shadow-2xl group">
              <video
                ref={videoElementRef}
                autoPlay
                loop
                playsInline
                preload="auto"
                className="w-full h-[400px] object-cover"
                onClick={() => {
                  try {
                    const v = videoElementRef.current;
                    if (v) {
                      v.muted = false;
                      setIsMuted(false);
                      v.play().catch(() => {});
                    }
                  } catch (e) {}
                }}
              >
                <source src={videoUrl} type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0a367b]/20 to-transparent"></div>

              {/* Mute/Unmute Button */}
              <motion.button
                onClick={toggleMute}
                className="absolute bottom-6 right-6 bg-white/10 backdrop-blur-md hover:bg-white/20 border border-white/30 rounded-full p-4 shadow-xl transition-all duration-300 opacity-100 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label={isMuted ? "إلغاء كتم الصوت" : "كتم الصوت"}
              >
                {isMuted ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                    />
                  </svg>
                )}
              </motion.button>
            </div>

            {/* Floating Elements around Video */}
            <motion.div
              className="absolute -top-6 -right-6 w-12 h-12 bg-[#dfa428]/60 rounded-full"
              animate={{
                rotate: [0, 360],
                scale: [1, 1.3, 1],
              }}
              transition={{ duration: 4, repeat: Infinity }}
            />
            <motion.div
              className="absolute -bottom-6 -left-6 w-8 h-8 bg-white/40 rounded-full"
              animate={{
                y: [0, -20, 0],
                scale: [1, 1.5, 1],
              }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </motion.div>

          {/* Content */}
          <motion.div
            className="text-white space-y-8"
            initial={{ opacity: 0, x: 100 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1.2, delay: 0.3 }}
          >
            <motion.h2
              className="text-5xl font-bold "
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              {title}
            </motion.h2>

            {titleHighlight && (
              <span className="text-5xl mb-5 font-bold  text-[#dfa428]">
                {" "}
                {titleHighlight}
              </span>
            )}

            <motion.p
              className="text-xl mt-10 leading-relaxed opacity-90"
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              {description}
            </motion.p>

            <motion.div
              className="flex space-x-4 space-x-reverse"
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.9 }}
            >
              <Link href="/ar-IQ/services">
                <motion.button
                  className="yellow-button bg-[#dfa428] text-[#0a367b] px-8 py-4  rounded-full font-bold hover:bg-yellow-500 transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  اكتشف المزيد
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default BotVideoSectiontwo;
